import {
  OktonEmptyState,
  OktonInput,
  OktonSearchFilter,
} from '@okton/platform-ui';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { FaBuilding, FaLock, FaPlus, FaStore, FaUsers } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { OktonReactTable as Table } from '@okton/platform-ui/react-table';

import AnalyticsPage from '../../layout/IntegraAppPage';
import backend from '../../services/backend';
import { usePermissoes } from '../../services/hooks/usePermissoes';
import AdminEmpresaSkeleton from './AdminEmpresaSkeleton';

type Filial = {
  id: number;
  nome: string;
  cnpj?: string | null;
};

type Usuario = {
  id: number;
  nome: string;
  email: string;
  filiais: number[];
};

type UsuarioRow = { row: { original: Usuario } };

function obterEmpresaIdUsuarioLogado(): number | null {
  const usuarioSalvo =
    localStorage.getItem('@DS850:user') || localStorage.getItem('user');

  if (!usuarioSalvo) return null;

  try {
    const usuario = JSON.parse(usuarioSalvo);
    const empresaId = Number(usuario?.empresa_id || usuario?.empresa);

    return empresaId > 0 ? empresaId : null;
  } catch {
    return null;
  }
}

const AdminEmpresa: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const { permite, Permissoes } = usePermissoes();

  const podeCriarFilial = permite(Permissoes.FILIAIS_CREATE);
  const podeGerenciarUsuarios = permite(Permissoes.USUARIOS_MANAGE);

  const {
    isOpen: isNovaFilialOpen,
    onOpen: onNovaFilialOpen,
    onClose: onNovaFilialClose,
  } = useDisclosure();

  const bgPage = useColorModeValue('white', 'gray.800');
  const pageWrapperBg = useColorModeValue('#EEF5F9', 'gray.900');
  const textColor = useColorModeValue('black', 'white');
  const pageText = useColorModeValue('gray.800', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const iconBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const iconColor = useColorModeValue('blue.600', 'cyan.200');

  const modalBg = useColorModeValue('white', 'gray.800');
  const modalText = useColorModeValue('gray.800', 'gray.100');

  const primaryButtonScheme = useColorModeValue('blue', 'cyan');

  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [busca, setBusca] = useState('');

  const [nomeFilial, setNomeFilial] = useState('');
  const [cnpjFilial, setCnpjFilial] = useState('');
  const [isCriandoFilial, setIsCriandoFilial] = useState(false);
  const [isVerificandoPermissao, setIsVerificandoPermissao] = useState(true);
  const [podeAcessarAdmin, setPodeAcessarAdmin] = useState(false);

  const empresaId = obterEmpresaIdUsuarioLogado();

  useEffect(() => {
    async function carregarDados() {
      try {
        setIsVerificandoPermissao(true);

        const permissaoResponse = await backend.get('/admin-empresa/permissao');
        const adminEmpresa = Boolean(permissaoResponse.data?.admin_empresa);

        setPodeAcessarAdmin(adminEmpresa);

        if (!adminEmpresa) return;

        const [filiaisResponse, usuariosResponse] = await Promise.all([
          backend.get('/admin-empresa/filiais'),
          backend.get('/admin-empresa/usuarios'),
        ]);

        setFiliais(filiaisResponse.data?.data || []);
        setUsuarios(usuariosResponse.data?.data || []);
      } catch (error) {
        const err = error as AxiosError<{ message?: string }>;

        toast({
          title:
            err.response?.data?.message ||
            'Erro ao carregar dados da administração.',
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      } finally {
        setIsVerificandoPermissao(false);
      }
    }

    carregarDados();
  }, [toast]);

  const usuariosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    if (!termo) return usuarios;

    return usuarios.filter(usuario => {
      return (
        usuario.nome.toLowerCase().includes(termo) ||
        usuario.email.toLowerCase().includes(termo)
      );
    });
  }, [usuarios, busca]);

  const fecharModalNovaFilial = () => {
    setNomeFilial('');
    setCnpjFilial('');
    onNovaFilialClose();
  };

  const criarFilial = async () => {
    if (!podeCriarFilial) return;

    if (!nomeFilial.trim()) {
      toast({
        title: 'Informe o nome da filial.',
        status: 'warning',
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }

    try {
      setIsCriandoFilial(true);

      const response = await backend.post('/admin-empresa/filiais', {
        nome: nomeFilial.trim(),
        cnpj: cnpjFilial.trim() || undefined,
      });

      const novaFilial = response.data?.data;

      setFiliais(prev => [
        ...prev,
        {
          id: Number(novaFilial.id),
          nome: novaFilial.nome,
          cnpj: novaFilial.cnpj,
        },
      ]);

      toast({
        title: response.data?.message || 'Filial criada com sucesso.',
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });

      fecharModalNovaFilial();
    } catch (error) {
      const err = error as AxiosError<{ message?: string }>;

      toast({
        title:
          err.response?.data?.message ||
          (error instanceof Error ? error.message : '') ||
          'Erro ao criar filial.',
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsCriandoFilial(false);
    }
  };

  const colunasUsuarios = useMemo(
    () => [
      {
        Header: 'USUÁRIO',
        accessor: 'nome',
        Cell: ({ row }: UsuarioRow) => {
          const usuario = row.original as Usuario;
          const iniciais = usuario.nome
            .split(' ')
            .slice(0, 2)
            .map(nome => nome[0])
            .join('')
            .toUpperCase();

          return (
            <HStack spacing={3}>
              <Flex
                w="34px"
                h="34px"
                align="center"
                justify="center"
                borderRadius="xl"
                bg="blue.100"
                color="blue.700"
                fontWeight="bold"
                fontSize="sm"
              >
                {iniciais || 'U'}
              </Flex>

              <Box>
                <Text fontWeight="bold" color={pageText}>
                  {usuario.nome}
                </Text>
                <Text fontSize="xs" color={mutedColor}>
                  ID {usuario.id}
                </Text>
              </Box>
            </HStack>
          );
        },
      },
      {
        Header: 'CONTATO',
        accessor: 'email',
        Cell: ({ value }: { value: string }) => (
          <Text color={pageText}>{value}</Text>
        ),
      },
      {
        Header: 'FILIAIS PERMITIDAS',
        accessor: 'filiais',
        Cell: ({ row }: UsuarioRow) => {
          const usuario = row.original as Usuario;

          if (usuario.filiais.length === 0) {
            return <Badge colorScheme="red">Sem filial</Badge>;
          }

          return (
            <HStack spacing={2} flexWrap="wrap">
              {usuario.filiais.map(filialId => {
                const filial = filiais.find(item => item.id === filialId);

                return (
                  <Badge key={filialId} colorScheme="blue">
                    {filial?.nome || `Filial ${filialId}`}
                  </Badge>
                );
              })}
            </HStack>
          );
        },
      },
      {
        Header: 'STATUS',
        accessor: 'status',
        Cell: ({ row }: UsuarioRow) => {
          const usuario = row.original as Usuario;
          const temFilial = usuario.filiais.length > 0;

          return (
            <Badge colorScheme={temFilial ? 'green' : 'orange'}>
              {temFilial ? 'Com acesso' : 'Pendente'}
            </Badge>
          );
        },
      },
      {
        Header: 'AÇÕES',
        id: 'acoes',
        Cell: ({ row }: UsuarioRow) =>
          podeGerenciarUsuarios ? (
            <Button
              size="sm"
              variant="ghost"
              colorScheme={primaryButtonScheme}
              onClick={() =>
                history.push(`/admin-empresa/usuarios/${row.original.id}`)
              }
            >
              Detalhes
            </Button>
          ) : (
            <Text fontSize="sm" color={mutedColor}>
              Sem permissão
            </Text>
          ),
      },
    ],
    [
      filiais,
      history,
      mutedColor,
      pageText,
      primaryButtonScheme,
      podeGerenciarUsuarios,
    ],
  );

  if (isVerificandoPermissao) {
    return <AdminEmpresaSkeleton />;
  }

  if (!podeAcessarAdmin) {
    return (
      <AnalyticsPage>
        <Flex align="center" justify="center" minH="60vh">
          <Box
            bg={bgPage}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            p={8}
            boxShadow="sm"
            maxW="520px"
            textAlign="center"
          >
            <Flex justify="center" mb={4}>
              <Flex bg={iconBg} color={iconColor} p={4} borderRadius="xl">
                <Icon as={FaLock} boxSize={6} />
              </Flex>
            </Flex>

            <Heading fontSize="2xl" mb={3}>
              Acesso restrito
            </Heading>
            <Text color={mutedColor}>
              Somente o administrador da empresa pode acessar esta área.
            </Text>
          </Box>
        </Flex>
      </AnalyticsPage>
    );
  }

  return (
    <AnalyticsPage>
      <Box bg={pageWrapperBg} minH="100vh" p={3}>
        <Flex justify="space-between" align="center" p={3}>
          <Box>
            <Heading fontWeight="bold" fontSize={25} color={textColor}>
              Administração da Empresa
            </Heading>
            <Text fontSize="sm" color={secondaryTextColor}>
              Gerencie filiais, usuários e permissões da empresa.
            </Text>
          </Box>

          {podeCriarFilial && (
            <Button
              colorScheme={primaryButtonScheme}
              leftIcon={<FaPlus />}
              onClick={onNovaFilialOpen}
              h="40px"
            >
              Nova filial
            </Button>
          )}
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <Box bg={bgPage} borderRadius="xl" p={6} boxShadow="sm">
            <Flex justify="space-between" align="flex-start">
              <Box>
                <Text fontSize="2xl" fontWeight="bold" color={pageText}>
                  #{empresaId || '-'}
                </Text>
                <Text color={mutedColor} fontWeight="medium">
                  Empresa atual
                </Text>
              </Box>

              <Icon as={FaBuilding} boxSize={5} color="blue.500" />
            </Flex>
          </Box>

          <Box bg={bgPage} borderRadius="xl" p={6} boxShadow="sm">
            <Flex justify="space-between" align="flex-start">
              <Box>
                <Text fontSize="2xl" fontWeight="bold" color={pageText}>
                  {filiais.length}
                </Text>
                <Text color={mutedColor} fontWeight="medium">
                  Filiais cadastradas
                </Text>
              </Box>

              <Icon as={FaStore} boxSize={5} color="green.500" />
            </Flex>
          </Box>

          <Box bg={bgPage} borderRadius="xl" p={6} boxShadow="sm">
            <Flex justify="space-between" align="flex-start">
              <Box>
                <Text fontSize="2xl" fontWeight="bold" color={pageText}>
                  {usuarios.length}
                </Text>
                <Text color={mutedColor} fontWeight="medium">
                  Usuários
                </Text>
              </Box>

              <Icon as={FaUsers} boxSize={5} color="orange.500" />
            </Flex>
          </Box>
        </SimpleGrid>

        <Flex
          justify="space-between"
          align="center"
          bg={bgPage}
          p={4}
          gap={4}
          flexDir="row"
          roundedTop="2xl"
          borderWidth="1px"
          borderBottomWidth={0}
        >
          <Flex w="100%">
            <OktonSearchFilter
              value={busca}
              onChange={setBusca}
              placeholder="Buscar por nome ou email"
              maxWidth={400}
            />
          </Flex>
        </Flex>

        <Box bg={bgPage} roundedBottom="2xl" p="4" pt="0" borderWidth="1px">
          <Table columns={colunasUsuarios} data={usuariosFiltrados} />

          {usuariosFiltrados.length === 0 && (
            <OktonEmptyState
              title="Nenhum usuário encontrado"
              description="Ajuste a busca ou cadastre um novo usuário."
            />
          )}
        </Box>
      </Box>

      <Modal
        isOpen={isNovaFilialOpen}
        onClose={fecharModalNovaFilial}
        isCentered
      >
        <ModalOverlay />
        <ModalContent bg={modalBg} color={modalText}>
          <ModalHeader>Nova filial</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Stack spacing={4}>
              <OktonInput
                name="nomeFilial"
                label="Nome da filial"
                required
                value={nomeFilial}
                onChange={event => setNomeFilial(event.target.value)}
                placeholder="Ex: Matriz, Loja 2, Depósito"
              />

              <OktonInput
                name="cnpjFilial"
                label="CNPJ"
                value={cnpjFilial}
                onChange={event => setCnpjFilial(event.target.value)}
                placeholder="Opcional"
              />
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={fecharModalNovaFilial}>
              Cancelar
            </Button>
            <Button
              colorScheme={primaryButtonScheme}
              onClick={criarFilial}
              isLoading={isCriandoFilial}
              isDisabled={!podeCriarFilial}
            >
              Criar filial
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AnalyticsPage>
  );
};

export default AdminEmpresa;
